import {
  createColumnHelper, flexRender, getCoreRowModel,
  getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import * as React from "react";
import { getCurrencySymbol } from "utils/currencySymbole";
import { convertToFloat } from "utils/formatNumber";
import ReactCountryFlag from "react-country-flag";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";

interface RowObj { key: string; value: string; }
const columnHelper = createColumnHelper<RowObj>();

function ADPaymentsList(props: {
  tableData: any; isLoading: boolean;
  timeFrame: any; setTimeFrame: any;
  type: string; dates: [Date, Date]; setDates: any;
}) {
  const { tableData, isLoading, type } = props;
  const isDecline = type === "decline";
  const isErrored = type === "errored";
  const isFailed = type === "failed";

  const columns = [
    columnHelper.accessor("key", {
      id: "key",
      header: () => <span className="adp-th-lbl">Currency</span>,
      cell: (info: any) => {
        const currency = info.getValue();
        const symbol = getCurrencySymbol(currency?.toUpperCase());
        return (
          <div className="adp-cur-cell">
            <div className="adp-flag-wrap">
              <ReactCountryFlag svg countryCode={symbol?.country_icon?.toUpperCase()} className="adp-flag" />
            </div>
            <div>
              <span className="adp-cur-code">{currency?.toUpperCase()}</span>
              {symbol?.title && <span className="adp-cur-name">{symbol.title}</span>}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("value", {
      id: "value",
      header: () => <span className="adp-th-lbl">Amount</span>,
      cell: (info: any) => (
        <div className="adp-amt-cell">
          <span className="adp-amt">{convertToFloat(info.getValue())}</span>
          <span className={`adp-cur-tag ${isDecline ? "adp-cur-tag--red" : isErrored ? "adp-cur-tag--errored" : isFailed ? "adp-cur-tag--failed" : "adp-cur-tag--green"}`}>
            {info?.row?.original?.key?.toUpperCase()}
          </span>
        </div>
      ),
    }),
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const table = useReactTable({
    data, columns, state: { sorting }, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
  });

  const total = tableData?.reduce((acc: number, row: any) => acc + (parseFloat(row.value) || 0), 0);

  return (
    <div className={`adp-card ${isDecline ? "adp-card--red" : isErrored ? "adp-card--errored" : isFailed ? "adp-card--failed" : "adp-card--green"}`}>
      <div className="adp-orb" />
      <div className="adp-shine" />

      {/* ── Header ── */}
      <div className="adp-hdr">
        <div className="adp-hl">
          <div className={`adp-ico ${isDecline ? "adp-ico--red" : isErrored ? "adp-ico--errored" : isFailed ? "adp-ico--failed" : "adp-ico--green"}`}>
            {isDecline ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            ) : isErrored || isFailed ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 7v6"/><circle cx="12" cy="16.5" r="1" fill="#fff" stroke="none"/>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            )}
          </div>
          <div>
            <h3 className="adp-title">{isDecline ? "Declined" : isErrored ? "Errored" : isFailed ? "Failed" : "Approved"} Payments</h3>
            <p className={`adp-subtitle ${isDecline ? "adp-subtitle--red" : isErrored ? "adp-subtitle--errored" : isFailed ? "adp-subtitle--failed" : "adp-subtitle--green"}`}>By currency breakdown</p>
          </div>
        </div>
        {!isLoading && total > 0 && (
          <div className={`adp-pill ${isDecline ? "adp-pill--red" : isErrored ? "adp-pill--errored" : isFailed ? "adp-pill--failed" : "adp-pill--green"}`}>
            {tableData?.length} {tableData?.length === 1 ? "currency" : "currencies"}
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div className={`adp-tbl-wrap ${isDecline ? "adp-tbl-wrap--red" : isErrored ? "adp-tbl-wrap--errored" : isFailed ? "adp-tbl-wrap--failed" : "adp-tbl-wrap--green"}`}>
        {isLoading ? (
          <div className="adp-loading">
            <div className={`adp-spinner ${isDecline ? "adp-spinner--red" : isErrored ? "adp-spinner--errored" : isFailed ? "adp-spinner--failed" : "adp-spinner--green"}`} />
            <p className="adp-loading-txt">Loading payments…</p>
          </div>
        ) : (
          <table className="adp-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className={`adp-thead-tr ${isDecline ? "adp-thead-tr--red" : isErrored ? "adp-thead-tr--errored" : isFailed ? "adp-thead-tr--failed" : "adp-thead-tr--green"}`}>
                  {hg.headers.map((h, hi) => (
                    <th key={h.id} colSpan={h.colSpan}
                      onClick={h.column.getToggleSortingHandler()}
                      className="adp-thead-th"
                      style={hi > 0 ? { textAlign: 'right' } : {}}>
                      <div className="adp-th-inner" style={hi > 0 ? { justifyContent: 'flex-end' } : {}}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="adp-sort-ico">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, ri) => (
                <tr key={row.id} className={`adp-tbody-tr ${isDecline ? "adp-tbody-tr--red" : isErrored ? "adp-tbody-tr--errored" : isFailed ? "adp-tbody-tr--failed" : "adp-tbody-tr--green"}`}
                  style={{ animationDelay: `${ri * 35}ms` }}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="adp-tbody-td">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
              {table.getRowModel().rows.length === 0 && (
                <tr><td colSpan={2}>
                  <div className="adp-empty">
                    <div className="adp-empty-icon">
                      {isDecline ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                        </svg>
                      )}
                    </div>
                    <p className="adp-empty-title">No {isDecline ? "declined" : isErrored ? "errored" : isFailed ? "failed" : "approved"} payments</p>
                    <p className="adp-empty-sub">No data for the selected filters</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer ── */}
      {!isLoading && tableData?.length > 0 && (
        <div className="adp-footer">
          <span className="adp-footer-lbl">{tableData.length} {tableData.length === 1 ? "currency" : "currencies"} listed</span>
          <div className={`adp-footer-dot ${isDecline ? "adp-footer-dot--red" : isErrored ? "adp-footer-dot--errored" : isFailed ? "adp-footer-dot--failed" : "adp-footer-dot--green"}`} />
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        /* ── Card shell ── */
        .adp-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          border-radius: 28px; overflow: hidden; display: flex; flex-direction: column;
          position: relative; height: 100%;
        }
        .adp-card--green { background: linear-gradient(160deg,#f0fff8 0%,#e6fff4 50%,#f5fffb 100%); border: 1.5px solid #b2f5db; }
        .adp-card--red   { background: linear-gradient(160deg,#fff5f5 0%,#ffe8e8 50%,#fff8f8 100%); border: 1.5px solid #ffc5c5; }
        .adp-card--errored { background: linear-gradient(160deg,#fffaf0 0%,#fff1d6 50%,#fffaf3 100%); border: 1.5px solid #ffd28a; }
        .adp-card--failed { background: linear-gradient(160deg,#f8f3ff 0%,#eee5ff 50%,#fbf8ff 100%); border: 1.5px solid #d7c3ff; }

        .adp-orb {
          position: absolute; top: -55px; right: -55px;
          width: 180px; height: 180px; border-radius: 50%; pointer-events: none; opacity: .08;
        }
        .adp-card--green .adp-orb { background: radial-gradient(circle,#00e5a8,transparent); }
        .adp-card--red   .adp-orb { background: radial-gradient(circle,#ff6b7a,transparent); }
        .adp-card--errored .adp-orb { background: radial-gradient(circle,#ff9f1c,transparent); }
        .adp-card--failed .adp-orb { background: radial-gradient(circle,#8b5cf6,transparent); }

        .adp-shine {
          position: absolute; inset: 0;
          background: linear-gradient(120deg,transparent 30%,rgba(255,255,255,.5) 50%,transparent 70%);
          animation: adpShine 4.5s ease-in-out infinite;
          pointer-events: none; border-radius: 28px; z-index: 0;
        }
        .adp-card--red .adp-shine { animation-delay: 2s; }
        .adp-card--errored .adp-shine { animation-delay: 1s; }
        .adp-card--failed .adp-shine { animation-delay: 3s; }
        @keyframes adpShine { 0%,100%{opacity:0;transform:translateX(-100%)} 50%{opacity:1;transform:translateX(100%)} }

        /* ── Header ── */
        .adp-hdr {
          padding: 16px 18px 12px; display: flex; align-items: center;
          justify-content: space-between; gap: 8px; flex-wrap: wrap;
          border-bottom: 1px solid rgba(0,0,0,.05); position: relative; z-index: 2;
        }
        .adp-hl { display: flex; align-items: center; gap: 10px; }
        .adp-ico {
          width: 42px; height: 42px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .adp-ico--green { background: linear-gradient(135deg,#00e5a8,#00a67c); box-shadow: 0 6px 20px rgba(0,198,150,.4),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .adp-ico--red   { background: linear-gradient(135deg,#ff7a87,#d63448); box-shadow: 0 6px 20px rgba(214,52,72,.4),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .adp-ico--errored { background: linear-gradient(135deg,#ffb347,#ff8c00); box-shadow: 0 6px 20px rgba(255,140,0,.4),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .adp-ico--failed { background: linear-gradient(135deg,#a78bfa,#7c3aed); box-shadow: 0 6px 20px rgba(124,58,237,.4),inset 0 1.5px 0 rgba(255,255,255,.5); }
        .adp-ico svg { filter: drop-shadow(0 1px 3px rgba(0,0,0,.2)); }

        .adp-title { font-size: 13px; font-weight: 800; color: #0d2137; letter-spacing: -.2px; line-height: 1.3; margin-bottom: 2px; }
        .adp-subtitle { font-size: 9.5px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; }
        .adp-subtitle--green { color: #00a67c; }
        .adp-subtitle--red   { color: #d63448; }
        .adp-subtitle--errored { color: #f08a00; }
        .adp-subtitle--failed { color: #7c3aed; }

        .adp-pill { font-size: 11px; font-weight: 900; padding: 5px 13px; border-radius: 100px; white-space: nowrap; }
        .adp-pill--green { background: linear-gradient(135deg,#00e5a8,#00b889); color: #003d2a; box-shadow: 0 4px 12px rgba(0,198,150,.35); }
        .adp-pill--red   { background: linear-gradient(135deg,#ff7a87,#e04055); color: #fff; box-shadow: 0 4px 12px rgba(214,52,72,.35); }
        .adp-pill--errored { background: linear-gradient(135deg,#ffb347,#ff8c00); color: #fff; box-shadow: 0 4px 12px rgba(255,140,0,.35); }
        .adp-pill--failed { background: linear-gradient(135deg,#a78bfa,#7c3aed); color: #fff; box-shadow: 0 4px 12px rgba(124,58,237,.35); }

        /* ── Table ── */
        .adp-tbl-wrap { overflow-y: auto; max-height: 340px; position: relative; z-index: 2; }
        .adp-tbl-wrap::-webkit-scrollbar { width: 4px; }
        .adp-tbl-wrap::-webkit-scrollbar-thumb { border-radius: 4px; background: rgba(0,0,0,.1); }

        .adp-table { width: 100%; border-collapse: collapse; }

        .adp-thead-tr { top: 0; z-index: 3; }
        .adp-thead-tr--green { background: linear-gradient(90deg,rgba(0,200,150,.1),rgba(0,200,150,.05)); }
        .adp-thead-tr--red   { background: linear-gradient(90deg,rgba(255,77,109,.1),rgba(255,77,109,.05)); }
        .adp-thead-tr--errored { background: linear-gradient(90deg,rgba(255,159,28,.12),rgba(255,159,28,.05)); }
        .adp-thead-tr--failed { background: linear-gradient(90deg,rgba(139,92,246,.12),rgba(139,92,246,.05)); }

        .adp-thead-th {
          padding: 10px 16px; text-align: left; cursor: pointer;
          user-select: none; white-space: nowrap;
          border-bottom: 1.5px solid rgba(0,0,0,.06);
        }
        .adp-th-inner { display: flex; align-items: center; gap: 5px; }
        .adp-th-lbl { font-size: 10px; font-weight: 800; letter-spacing: .6px; text-transform: uppercase; color: #344563; }
        .adp-sort-ico { opacity: .4; display: flex; align-items: center; }

        /* Row animation */
        @keyframes adpFadeIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:none} }

        .adp-tbody-tr {
          transition: transform .12s, background .12s;
          animation: adpFadeIn .3s ease both;
        }
        .adp-tbody-tr--green:hover { background: rgba(0,200,150,.07); transform: translateX(3px); }
        .adp-tbody-tr--red:hover   { background: rgba(255,77,109,.07); transform: translateX(3px); }
        .adp-tbody-tr--errored:hover { background: rgba(255,159,28,.08); transform: translateX(3px); }
        .adp-tbody-tr--failed:hover { background: rgba(139,92,246,.08); transform: translateX(3px); }
        .adp-tbody-tr:not(:last-child) .adp-tbody-td { border-bottom: 1px solid rgba(0,0,0,.04); }

        .adp-tbody-td { padding: 11px 16px; vertical-align: middle; }

        /* Currency cell */
        .adp-cur-cell { display: flex; align-items: center; gap: 10px; }
        .adp-flag-wrap {
          width: 34px; height: 34px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden;
          border: 1.5px solid rgba(0,0,0,.06); background: rgba(255,255,255,.7);
          backdrop-filter: blur(4px);
        }
        .adp-flag { font-size: 18px !important; }
        .adp-cur-code { display: block; font-size: 12.5px; font-weight: 800; color: #0d2137; line-height: 1.2; }
        .adp-cur-name { display: block; font-size: 10.5px; font-weight: 500; color: #5a7090; margin-top: 1px; }

        /* Amount cell */
        .adp-amt-cell { display: flex; align-items: center; gap: 7px; justify-content: flex-end; }
        .adp-amt { font-size: 13.5px; font-weight: 900; color: #0d2137; letter-spacing: -.3px; }
        .adp-cur-tag { font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 8px; }
        .adp-cur-tag--green { background: rgba(0,200,150,.12); color: #00875f; }
        .adp-cur-tag--red   { background: rgba(255,77,109,.12); color: #b01020; }
        .adp-cur-tag--errored { background: rgba(255,159,28,.14); color: #c66a00; }
        .adp-cur-tag--failed { background: rgba(139,92,246,.14); color: #6d28d9; }

        /* Footer */
        .adp-footer {
          padding: 10px 18px; border-top: 1px solid rgba(0,0,0,.05);
          display: flex; align-items: center; justify-content: space-between;
          position: relative; z-index: 2;
        }
        .adp-footer-lbl { font-size: 11px; font-weight: 700; color: #5a7090; }
        .adp-footer-dot {
          width: 6px; height: 6px; border-radius: 50%;
          animation: adpPulse 2s ease-in-out infinite;
        }
        .adp-footer-dot--green { background: #00c896; }
        .adp-footer-dot--red   { background: #ff4d6d; }
        .adp-footer-dot--errored { background: #ff9f1c; }
        .adp-footer-dot--failed { background: #8b5cf6; }
        @keyframes adpPulse { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.4)} }

        /* States */
        .adp-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 12px; }
        .adp-spinner { width: 26px; height: 26px; border-radius: 50%; border: 2.5px solid transparent; animation: adpSpin .7s linear infinite; }
        .adp-spinner--green { border-color: #b2f5db; border-top-color: #00a67c; }
        .adp-spinner--red   { border-color: #ffc5c5; border-top-color: #d63448; }
        .adp-spinner--errored { border-color: #ffd9a3; border-top-color: #ff8c00; }
        .adp-spinner--failed { border-color: #d7c3ff; border-top-color: #7c3aed; }
        @keyframes adpSpin { to { transform: rotate(360deg); } }
        .adp-loading-txt { font-size: 12.5px; color: #5a7090; font-weight: 600; }

        .adp-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 24px; gap: 10px; }
        .adp-empty-icon {
          width: 48px; height: 48px; border-radius: 13px;
          background: rgba(255,255,255,.8); border: 1.5px solid rgba(0,0,0,.06);
          display: flex; align-items: center; justify-content: center; color: #b0bae8;
        }
        .adp-empty-title { font-size: 13px; font-weight: 800; color: #475080; }
        .adp-empty-sub { font-size: 11.5px; color: #8a96b8; font-weight: 500; }
      `}</style>
    </div>
  );
}

export default ADPaymentsList;